import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PropertyCard from "@/components/PropertyCard";
import { useSettings } from "@/context/SettingsContext";
import { OfferBanner } from "@/components/OfferBanner";
import { ArrowRight, Home, Key, FileCheck, Users } from "lucide-react";

const DEFAULT_HERO_BG = "/hero-loroux.jpg";
const VINEYARD_BG = "/hero-loroux.jpg";
const AGENT_IMAGE = "/equipe-mainguy.jpg";
const AGENT_IMAGE_WEBP = "/equipe-mainguy.webp";

export const HomePage = () => {
  const { settings } = useSettings();
  const heroBg = settings.hero_image || DEFAULT_HERO_BG;
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/properties?featured=true`);
        setFeaturedProperties(response.data.slice(0, 6));
      } catch (error) {
        console.error("Error fetching featured properties:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);
  
  const services = [
    {
      icon: Home,
      title: "Achat immobilier",
      description: "Trouvez la maison de vos rêves parmi notre sélection de biens d'exception."
    },
    {
      icon: Key,
      title: "Location",
      description: "Des appartements et maisons à louer, sélectionnés avec soin pour vous."
    },
    {
      icon: FileCheck,
      title: "Estimation gratuite",
      description: "Obtenez une estimation précise de votre bien en moins de 48h."
    },
    {
      icon: Users,
      title: "Accompagnement",
      description: "Un suivi personnalisé de A à Z pour votre projet immobilier."
    }
  ];
  
  return (
    <div className="min-h-screen" data-testid="home-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-28 pb-16 md:pt-32 md:pb-20" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src={heroBg} 
            alt="Maison en pierre dans le vignoble nantais, Le Loroux-Bottereau" 
            className="w-full h-full object-cover"
            data-testid="hero-image"
          />
          <div className="hero-watermark absolute inset-0" data-testid="hero-watermark" />
        </div>
        
        <div className="container-custom relative z-10 text-center text-[#0A0A0A]">
          <p className="font-cinzel text-sm md:text-base uppercase tracking-[0.3em] text-[#B8952B] mb-4 animate-fade-in">
            Bienvenue chez
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-none mb-5 animate-slide-up">
            <span className="block text-[#0A0A0A]" data-testid="hero-title-mainguy">MAINGUY</span>
            <span className="block text-[#B8952B]" data-testid="hero-title-immobilier">IMMOBILIER</span>
          </h1>
          <p className="text-base md:text-lg text-gray-700 font-medium max-w-2xl mx-auto mb-7 animate-slide-up stagger-2">
            L'excellence immobilière au service de vos projets. 
            Découvrez des biens d'exception au Loroux-Bottereau et dans le vignoble nantais.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up stagger-3">
            <Link 
              to="/catalogue?type=vente" 
              className="btn-primary"
              data-testid="hero-cta-buy"
            >
              Acheter un bien
            </Link>
            <Link 
              to="/catalogue?type=location" 
              className="bg-transparent text-[#0A0A0A] border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#D4AF37] transition-all duration-300 px-8 py-3 uppercase tracking-wider"
              data-testid="hero-cta-rent"
            >
              Louer un bien
            </Link>
          </div>

          <div className="mt-10 md:mt-12 animate-slide-up stagger-3">
            <OfferBanner />
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
              Nos services
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-[#0A0A0A]">
              Un accompagnement sur mesure
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div 
                key={service.title}
                className="text-center p-8 hover:bg-[#F9F9F9] transition-colors rounded-sm border border-transparent hover:border-[#D4AF37]/20"
                data-testid={`service-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37] rounded-full flex items-center justify-center">
                  <service.icon size={28} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-xl mb-3 text-[#0A0A0A]">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Featured Properties */}
      <section className="section-padding bg-[#F9F9F9]" data-testid="featured-properties-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
                Sélection
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-[#0A0A0A]">
                Nos coups de cœur
              </h2>
            </div>
            <Link 
              to="/catalogue" 
              className="btn-ghost flex items-center gap-2 group"
              data-testid="view-all-properties"
            >
              Voir tous les biens
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-sm overflow-hidden">
                  <div className="skeleton h-64" />
                  <div className="p-5 space-y-3">
                    <div className="skeleton h-6 w-3/4" />
                    <div className="skeleton h-4 w-1/2" />
                    <div className="skeleton h-8 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="featured-properties-grid">
              {featuredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* About Section */}
      <section className="relative py-32 overflow-hidden" data-testid="about-section">
        <div className="absolute inset-0">
          <img 
            src={VINEYARD_BG} 
            alt="Vignoble" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
                Notre histoire
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-6" data-testid="about-title">
                L’immobilier, avec
                <span className="block text-[#D4AF37]">proximité et transparence</span>
              </h2>
              <p className="text-white font-medium leading-relaxed mb-5">
                Bienvenue chez MAINGUY Immobilier.
              </p>
              <p className="text-gray-300 leading-relaxed mb-5">
                Implantée au Loroux-Bottereau, au cœur du Vignoble nantais, notre agence à taille humaine
                vous accompagne dans vos projets de vente, d’achat et de gestion locative, du Vignoble
                nantais à la région nantaise et jusqu’à la côte Atlantique.
              </p>
              <p className="text-gray-300 leading-relaxed mb-5">
                <span className="text-[#D4AF37] font-semibold">Julie</span>, gérante de MAINGUY Immobilier, est votre interlocutrice privilégiée et vous
                accompagne personnellement dans chacun de vos projets. Forte de son expérience dans l’immobilier
                et particulièrement en gestion locative, elle privilégie une approche fondée sur l’écoute, la
                disponibilité et la transparence.
              </p>
              <p className="text-gray-300 leading-relaxed mb-5">
                <span className="text-[#D4AF37] font-semibold">Médéric</span>, cofondateur de l’agence, partage cette volonté de créer une entreprise
                indépendante, locale et profondément attachée à son territoire.
              </p>
              <p className="text-gray-300 leading-relaxed mb-6">
                Parce que derrière chaque projet se trouve une histoire, un patrimoine ou un investissement,
                nous croyons qu’un projet immobilier mérite bien plus qu’une simple transaction : des conseils
                sincères, un accompagnement attentif et une relation de confiance.
              </p>
              <p className="font-serif text-lg md:text-xl text-[#D4AF37] mb-8" data-testid="about-signature">
                Une agence locale. Un accompagnement sur mesure. Une parole qui compte.
              </p>
              <Link 
                to="/contact" 
                className="btn-primary inline-flex items-center gap-2"
                data-testid="about-cta"
              >
                Nous rencontrer
                <ArrowRight size={16} />
              </Link>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <picture>
                  <source srcSet={AGENT_IMAGE_WEBP} type="image/webp" />
                  <img 
                    src={AGENT_IMAGE} 
                    alt="Julie et Médéric MAINGUY" 
                    className="w-full h-full object-cover object-center"
                    width={1679}
                    height={2000}
                    decoding="async"
                    data-testid="team-photo"
                  />
                </picture>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] text-[#0A0A0A] p-6">
                <p className="font-serif text-3xl font-bold" data-testid="experience-years">6+</p>
                <p className="text-sm uppercase tracking-wider">Années d'expérience</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="cta-section">
        <div className="container-custom text-center">
          <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Prêt à commencer ?
          </p>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white mb-6">
            Estimez votre bien gratuitement
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-10">
            Recevez une estimation précise de votre bien en moins de 48h. 
            Nos experts sont à votre disposition pour vous accompagner.
          </p>
          <Link 
            to="/estimation" 
            className="btn-primary inline-flex items-center gap-2"
            data-testid="estimate-cta"
          >
            Demander une estimation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default HomePage;
